// @ts-check
/**
 * Time menu: query block child menu wrapper for the date range picker widget
 */
import {SelectorType} from 'common/widgets/query-block/constants';
import {getCurrentMoment} from 'common/util/date';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DATE_RANGE_TYPES} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {DatePickerMode, getDatePickerMode, getDateRangeLabel} from 'common/widgets/date-range-picker-new/util';
import {ChildMenu} from 'common/widgets/query-block/child-menu';

import template from './index.jade';

interface TimeMenuState {
  selected: DateRange;
}

export class TimeMenu extends ChildMenu<TimeMenuState> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'display-context': {type: `string`, default: DisplayContext.FullLength},
      'selector-type': {type: `string`, default: SelectorType.Text, enum: Object.values(SelectorType)},
      'project-as-of-date': {
        type: `string`,
        default: ``,
        description: `as of date for projects that do not have real time ingestion`,
      },
      'max-data-history-days': {
        type: `number`,
        default: 0,
        description: `max days time menu can query into data history`,
      },
    };
  }

  get config() {
    return {
      template,
      helpers: {
        ...super.config.helpers,
        getDateRangeAttributes: () => this.state.selected?.getAttributes(),
        onDateApply: ({detail: {dateRange}}): void => {
          this.dispatchEvent(new CustomEvent(`select`, {detail: dateRange}));
          this.trackSelection(this.trackingProps(dateRange));
        },
        onDateCancel: (): void => {
          this.dispatchEvent(new CustomEvent(`cancel`));
        },
      },
    };
  }

  getPrefixLabel(): string {
    const dateRange = this.state.selected;
    const matchingPreset = this.getPresetMatch();

    if (!dateRange) {
      return ``;
    }

    if (matchingPreset) {
      return `in the last`;
    }

    const pickerMode = getDatePickerMode(dateRange);
    switch (pickerMode) {
      case DatePickerMode.Between:
      case DatePickerMode.On:
        return `from`;
      case DatePickerMode.Since:
        return `since`;
      case DatePickerMode.InTheLast:
        return `in the last`;
      default: {
        const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
        throw new Error(`unknown picker mode ${unreachable}`);
      }
    }
  }

  getPresetMatch() {
    const {selected} = this.state;

    if (!selected) {
      return null;
    }

    const dateRangeClass = DATE_RANGE_TYPES[this.attr(`display-context`)];
    return dateRangeClass.getPresetOptions().find((option) => {
      return selected.equals(new DateRange(option.value));
    });
  }

  selectedLabel(): string {
    return getDateRangeLabel(
      this.state.selected,
      this.getPresetMatch(),
      null /* project as of date; we set this to "null" because we're trialing a change where we show the project-as-of-date in the tooltip, not the label */,
      getCurrentMoment({localized: true}),
      this.attr(`selector-type`) === `text` ? `none` : `simple`,
    );
  }

  transformSelectedVal(val): DateRange {
    return val ? new DATE_RANGE_TYPES[this.attr(`display-context`)](val) : null;
  }

  isContentSearchable() {
    return false;
  }

  trackingProps(dateRange) {
    return {
      eventName: `Select date range`,
      props: {
        'date range from': dateRange.from || null,
        'date range to': dateRange.to || null,
        'date range type': dateRange.type,
        'window unit': dateRange.window ? dateRange.window.unit : null,
        'window value': dateRange.window ? dateRange.window.value : null,
      },
    };
  }
}

export const TimeMenuEl = defineAndMakeJsxFactory(`mp-time-menu`, TimeMenu);
